import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import EditModal from 'components/UI/EditModal';
import Button from 'components/UI/Button';
import { ButtonGrid } from './styled';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { ConnectivityDetails, DesiredValue, DeviceShadowFeature } from 'models';
import DesiredFamily from '../DesiredFamily';
import Loader from 'components/UI/Loader';
import {
  connectivityDetailsSelector,
  fetchDeviceShadow,
  removeDesiredValues,
  storeDesiredValues,
} from 'store/connectivityState/connectivityState';
import DesiredConfirmation from '../DesiredConfirmation';

type Props = {
  selectedFamily: DeviceShadowFeature;
  specificationId: string;
  open: boolean;
  onClose: () => void;
};

const UpdateDesiredModal: FC<Props> = ({
  selectedFamily,
  specificationId,
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm();
  const { desired }: ConnectivityDetails = useAppSelector(
    connectivityDetailsSelector
  );
  const [desiredValues, setDesiredValues] = useState<DesiredValue[]>([]);
  const { id } = useParams();
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);

  const loadingSpinner = useMemo(() => {
    if (desired.isLoading) {
      return <Loader center />;
    }
  }, [desired.isLoading]);

  const getShadowPart = useCallback(
    (
      shadowPart: DeviceShadowFeature,
      properties: string[]
    ): DeviceShadowFeature | undefined => {
      if (!shadowPart || properties.length === 0) {
        return shadowPart;
      }

      const nextShadowPart = shadowPart?.children?.find(
        (item: DeviceShadowFeature) => item.title === properties[0]
      );
      if (!nextShadowPart) {
        return undefined;
      }
      return getShadowPart(nextShadowPart, properties.slice(1));
    },
    []
  );

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values: FieldValues) => {
      if (values === undefined) {
        return;
      }
      const changedValues: DesiredValue[] = [];
      Object.keys(values).forEach((propertyKey) => {
        const propertyKeyParts = propertyKey.split('/');
        if (selectedFamily?.title === propertyKeyParts[0]) {
          if (values[propertyKey] !== '') {
            changedValues.push({
              resourceId: propertyKey,
              value: values[propertyKey],
            });
          }
        }
      });
      if (changedValues.length > 0) {
        setDesiredValues(changedValues);
        setConfirmationOpen(true);
      }
    },
    [selectedFamily?.title]
  );

  const onConfirm = useCallback(() => {
    if (id && desiredValues) {
      dispatch(
        storeDesiredValues(id, 'happ-device', specificationId, desiredValues)
      );
      setTimeout(() => {
        dispatch(fetchDeviceShadow(id, 'happ-device', specificationId));
      }, 1000);
      // 'happ-device' is currently the only supported feature
    }
    onClose();
  }, [id, desiredValues, onClose, dispatch, specificationId]);

  const handleDesiredRemoved = useCallback(
    (iprId: string, resourceId: string) => {
      if (iprId) {
        dispatch(removeDesiredValues(iprId, 'happ-device', resourceId));
        dispatch(fetchDeviceShadow(iprId, 'happ-device', specificationId));
      }
    },
    [dispatch, specificationId]
  );
  return (
    <EditModal title="device_desired_values.title" open={open} close={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {loadingSpinner}
        <DesiredFamily
          id={id as string}
          family={selectedFamily}
          register={register}
          errors={errors}
          onDesiredRemoved={handleDesiredRemoved}
        />
        <ButtonGrid>
          <Button
            onClick={onClose}
            backgroundColor="transparent"
            color="primary"
          >
            <FormattedMessage id="modal.btn_close" />
          </Button>
          <Button submit>
            <FormattedMessage id="device_desired_values.btn_save" />
          </Button>
        </ButtonGrid>
      </form>
      <DesiredConfirmation
        desiredValues={desiredValues}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={onConfirm}
      />
    </EditModal>
  );
};

export default UpdateDesiredModal;
